import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getMyProfile, saveMyProfile, getTimeSlots } from '../../components/apiClient.js';
import ProfileNavigation from './ProfileNavigation';

interface StudyPreferencesData {
  preferredTime: 'morning' | 'afternoon' | 'evening' | 'night';
  studyHoursPerDay: number;
  studySlots: string[];
}

const StudyPreferences: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<any>(null);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Initialize React Hook Form
  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    setValue
  } = useForm<StudyPreferencesData>({
    defaultValues: {
      preferredTime: 'morning',
      studyHoursPerDay: 6,
      studySlots: []
    },
    mode: 'onChange'
  });

  // Load existing preferences and time slots from API
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        // Load profile data
        const response = await getMyProfile();
        if (response.status === 'success' && response.result) {
          const profileData = response.result;
          setProfile(profileData);

          if (profileData.study_pattern) {
            // Parse study pattern data if it exists
            const studyData = typeof profileData.study_pattern === 'string'
              ? JSON.parse(profileData.study_pattern)
              : profileData.study_pattern;

            // Set form values from profile data
            setValue('preferredTime', studyData.preferredTime || 'morning');
            setValue('studyHoursPerDay', studyData.studyHoursPerDay || 6);
            setValue('studySlots', studyData.studySlots || []);
          }
        }

        // Load time slots
        const slotsResponse = await getTimeSlots();
        if (slotsResponse.status === 'success' && slotsResponse.result) {
          setTimeSlots(slotsResponse.result || []);
        }
      } catch (error) {
        console.error('Failed to load study preferences:', error);
      }
    };

    loadPreferences();
  }, [setValue]);

  // Form submission handler
  const onSubmit = async (data: StudyPreferencesData) => {
    if (isSaving) return;

    try {
      setIsSaving(true);
      console.log('Saving study preferences:', data);

      // Merge form data with existing profile to prevent data loss
      const updatedProfile = {
        ...profile,
        study_pattern: data
      };

      const response = await saveMyProfile(updatedProfile);

      if (response.status === 'success') {
        setLastSaved(new Date());
        setProfile(updatedProfile);
      } else {
        throw new Error(response.message || 'Failed to save study preferences');
      }
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSaving(false);
    }
  };



  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '10px' }}>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-10 col-lg-12">
            <ProfileNavigation className="mb-4" />
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h2 className="fw-bold mb-1">
                  <i className="fas fa-cog me-2 text-primary"></i>
                  Study Preferences
                </h2>
                <p className="text-muted mb-0">Customize your study schedule and preferences</p>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => navigate('/profile')}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Profile
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="row g-4">
                {/* Preferred Study Time */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-clock me-2"></i>
                        Preferred Study Time
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="preferredTime"
                        control={control}
                        rules={{ required: 'Please select your preferred study time' }}
                        render={({ field, fieldState }) => (
                          <>
                            <div className="row g-3">
                              {[
                                { value: 'morning', label: 'Morning Person', icon: 'fa-sun', color: 'warning' },
                                { value: 'afternoon', label: 'Afternoon Learner', icon: 'fa-cloud-sun', color: 'info' },
                                { value: 'evening', label: 'Evening Student', icon: 'fa-moon', color: 'secondary' },
                                { value: 'night', label: 'Night Owl', icon: 'fa-star', color: 'dark' }
                              ].map((option) => (
                                <div key={option.value} className="col-md-6 col-lg-3">
                                  <div
                                    className={`card border-2 cursor-pointer ${field.value === option.value
                                        ? `border-${option.color} bg-light`
                                        : 'border-light'
                                      }`}
                                    onClick={() => field.onChange(option.value)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="card-body text-center p-3">
                                      <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center bg-${option.color} ${field.value === option.value ? 'text-white' : 'bg-opacity-20'}`}
                                        style={{ width: '60px', height: '60px' }}>
                                        <i className={`fas ${option.icon} fa-lg`}></i>
                                      </div>
                                      <h6 className={`mb-0 ${field.value === option.value ? `text-${option.color}` : ''}`}>
                                        {option.label}
                                      </h6>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {fieldState.error && (
                              <div className="alert alert-danger mt-3">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Study Slots */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-calendar-alt me-2"></i>
                        Study Slots
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="studySlots"
                        control={control}
                        rules={{ required: 'Please select at least one study slot' }}
                        render={({ field, fieldState }) => (
                          <>
                            <div className="row g-3">
                              {timeSlots.map((slot) => (
                                <div key={slot.id} className="col-md-6 col-lg-4">
                                  <div
                                    className={`card border-2 cursor-pointer ${field.value?.includes(slot.id.toString())
                                        ? 'border-info bg-light'
                                        : 'border-light'
                                      }`}
                                    onClick={() => {
                                      const currentSlots = field.value || [];
                                      const slotId = slot.id.toString();
                                      if (currentSlots.includes(slotId)) {
                                        field.onChange(currentSlots.filter(id => id !== slotId));
                                      } else {
                                        field.onChange([...currentSlots, slotId]);
                                      }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div className="card-body text-center p-3">
                                      <div className={`rounded-circle mx-auto mb-2 d-flex align-items-center justify-content-center ${field.value?.includes(slot.id.toString()) ? 'bg-info text-white' : 'bg-light'}`}
                                        style={{ width: '50px', height: '50px' }}>
                                        <i className="fas fa-clock"></i>
                                      </div>
                                      <h6 className={`mb-1 ${field.value?.includes(slot.id.toString()) ? 'text-info' : ''}`}>
                                        {slot.slot_name}
                                      </h6>
                                      <small className="text-muted">{slot.start_time} - {slot.end_time}</small>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {fieldState.error && (
                              <div className="alert alert-danger mt-3">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Study Hours Per Day */}
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-hourglass-half me-2"></i>
                        Daily Study Hours
                      </h5>
                    </div>
                    <div className="card-body">
                      <Controller
                        name="studyHoursPerDay"
                        control={control}
                        rules={{
                          required: 'Please set your daily study hours',
                          min: { value: 1, message: 'Minimum 1 hour required' },
                          max: { value: 16, message: 'Maximum 16 hours allowed' }
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <div className="mb-3">
                              <label className="form-label fw-medium">Hours per day</label>
                              <input
                                {...field}
                                type="range"
                                className="form-range"
                                min="1"
                                max="16"
                                onChange={(e) => field.onChange(parseInt(e.target.value))}
                              />
                              <div className="d-flex justify-content-between text-muted small">
                                <span>1h</span>
                                <span>8h</span>
                                <span>16h</span>
                              </div>
                            </div>
                            <div className="text-center">
                              <h4 className="text-success mb-0">{field.value} hours/day</h4>
                              <small className="text-muted">
                                {field.value * 7} hours per week
                              </small>
                            </div>
                            {fieldState.error && (
                              <div className="alert alert-danger mt-3">
                                {fieldState.error.message}
                              </div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </div>
                </div>


              </div>

              {/* Save Button */}
              <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                <div className="d-flex align-items-center">
                  {lastSaved && (
                    <small className="text-muted">
                      <i className="fas fa-check text-success me-1"></i>
                      Last saved: {lastSaved.toLocaleTimeString()}
                    </small>
                  )}
                  {isDirty && !lastSaved && (
                    <small className="text-warning">
                      <i className="fas fa-exclamation-triangle me-1"></i>
                      You have unsaved changes
                    </small>
                  )}
                </div>
                <button
                  type="submit"
                  className={`btn btn-primary ${isSaving ? 'disabled' : !isValid ? 'disabled' : ''
                    }`}
                  disabled={isSaving || !isValid}
                >
                  {isSaving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save me-2"></i>
                      Save Preferences
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPreferences;